import React from "react";
import { Typography, Paper, Box } from "@mui/material";
import { motion } from "framer-motion";
import {
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from "@mui/lab";
import WorkIcon from "@mui/icons-material/Work";

const ExperienceCard = ({ year, title, company, description, delay = 0 }) => (
  <TimelineItem sx={{ minHeight: 140, mb: 2 }}>
    {/* Timeline Visuals (Dot & Line) */}
    <TimelineSeparator>
      <TimelineDot
        sx={{
          bgcolor: "rgba(30, 136, 229, 0.1)",
          border: "2px solid",
          borderColor: "primary.main",
          boxShadow: "0 0 20px rgba(30, 136, 229, 0.5)",
          p: 1.2,
          transition: "all 0.3s ease",
          "&:hover": {
            bgcolor: "primary.main",
            boxShadow: "0 0 30px rgba(30, 136, 229, 0.8)",
            "& svg": { color: "#fff" },
          },
        }}
      >
        <WorkIcon
          sx={{
            color: "primary.light",
            fontSize: "1.2rem",
            transition: "color 0.3s ease",
          }}
        />
      </TimelineDot>
      <TimelineConnector
        sx={{
          background:
            "linear-gradient(to bottom, rgba(30,136,229,0.6) 0%, transparent 100%)",
          width: 2,
          opacity: 0.7,
        }}
      />
    </TimelineSeparator>

    {/* Card Content */}
    <TimelineContent sx={{ py: 1, px: { xs: 2, sm: 4 } }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }} // Custom spring-like ease
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            background:
              "linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.06)",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            textAlign: "left",
            // Sexy Hover Effect
            "&:hover": {
              transform: "translateY(-5px) scale(1.02)",
              borderColor: "rgba(30, 136, 229, 0.3)",
              background:
                "linear-gradient(145deg, rgba(30, 136, 229, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)",
              boxShadow: "0 15px 40px -10px rgba(30, 136, 229, 0.2)",
            },
          }}
        >
          {/* Ambient Glow Inside Card */}
          <Box
            sx={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(30,136,229,0.15) 0%, transparent 70%)",
              borderRadius: "50%",
              pointerEvents: "none",
            }}
          />

          {/* Year Badge */}
          <Box
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: "rgba(30, 136, 229, 0.15)",
              border: "1px solid rgba(30, 136, 229, 0.2)",
              mb: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "primary.light",
                fontWeight: 600,
                letterSpacing: 1.5,
              }}
            >
              {year}
            </Typography>
          </Box>

          {/* Main Info */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              color: "text.primary",
              letterSpacing: "0.2px",
              fontSize:{xs: "1rem"}
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "primary.main", mb: 2, fontWeight: 500 ,  fontSize:{xs: ".8rem"} }}
          >
            {company}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.8,
              fontSize: "0.8rem",
            }}
          >
            {description}
          </Typography>
        </Paper>
      </motion.div>
    </TimelineContent>
  </TimelineItem>
);

export default ExperienceCard;
